import { create } from 'zustand';

import { ServiceLevel, IServiceLevelEntry } from '../models/serviceLevel';

export type TServiceLevelsSortBy = 'name' | 'createdAt' | 'updatedAt';

// Re-export for backward compatibility
export type { IServiceLevelEntry };

interface IServiceLevelsStore {
  rowData: ServiceLevel[];
  pageSize: number;
  offset: number;
  sortBy: TServiceLevelsSortBy;
  order: 'ASC' | 'DESC';
  defaultSort: {
    sortBy: TServiceLevelsSortBy;
    order: 'ASC' | 'DESC';
  };
  error: string | boolean;
  loading: boolean;
  count: number;
  total: number;
  dispatch: (args: TServiceLevelsReducerInput) => void;
  // Legacy support for ShipmentsListView
  serviceLevels: Array<IServiceLevelEntry>;
  setServiceLevels: (input: Array<IServiceLevelEntry>) => void;
  refreshKey: number;
}

type TServiceLevelsReducerInput =
  | {
      type: 'updateRowData';
      args: { rowData: ServiceLevel[] };
    }
  | {
      type: 'updateError';
      args: { error: string | boolean };
    }
  | {
      type: 'updateLoading';
      args: { loading: boolean };
    }
  | {
      type: 'updatePagination';
      args: { pageSize: number; offset: number };
    }
  | {
      type: 'updateSorting';
      args: {
        sortBy: TServiceLevelsSortBy;
        order: 'ASC' | 'DESC';
      };
    }
  | {
      type: 'updateCount';
      args: { count: number };
    }
  | {
      type: 'updateTotal';
      args: { total: number };
    }
  | {
      type: 'reset';
    }
  | { type: 'refresh' };

const serviceLevelsReducer = (
  state: IServiceLevelsStore,
  input: TServiceLevelsReducerInput
) => {
  switch (input.type) {
    case 'updateRowData':
      return { ...state, rowData: input.args.rowData };
    case 'updateError':
      return { ...state, error: input.args.error };
    case 'updateLoading':
      return { ...state, loading: input.args.loading };
    case 'updatePagination':
      return {
        ...state,
        pageSize: input.args.pageSize,
        offset: input.args.offset,
      };
    case 'updateSorting':
      return { ...state, sortBy: input.args.sortBy, order: input.args.order };
    case 'updateCount':
      return { ...state, count: input.args.count };
    case 'updateTotal':
      return { ...state, total: input.args.total };
    case 'reset':
      return INITIAL_STATE;
    case 'refresh':
      return { ...state, refreshKey: state.refreshKey + 1 };
  }
};

const INITIAL_STATE: Omit<
  IServiceLevelsStore,
  'dispatch' | 'setServiceLevels'
> = {
  rowData: [],
  error: '',
  loading: false,
  pageSize: 10,
  offset: 0,
  sortBy: 'name',
  order: 'ASC',
  defaultSort: {
    sortBy: 'name',
    order: 'ASC',
  },
  count: 0,
  total: 0,
  serviceLevels: [],
  refreshKey: 0,
};

const useServiceLevelsStore = create<IServiceLevelsStore>((set) => ({
  ...INITIAL_STATE,
  dispatch: (input) => set((state) => serviceLevelsReducer(state, input)),
  setServiceLevels: (input) => set(() => ({ serviceLevels: input })),
}));

export default useServiceLevelsStore;
